import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  ArrowUpCircle,
  Building2,
  CalendarClock,
  Download,
  Loader2,
  MoreHorizontal,
  Puzzle,
  Settings,
  Upload,
  User,
  Users,
} from "lucide-react";

import { cn } from "@/infrastructure/lib/utils";
import { useToast } from "@/shared/hooks/use-toast";
import { Avatar, AvatarFallback, AvatarImage } from "@/shared/components/ui/avatar";
import { Badge } from "@/shared/components/ui/badge";
import { Button } from "@/shared/components/ui/button";
import { Card, CardContent } from "@/shared/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/shared/components/ui/tooltip";
import { useViewScope } from "@/shared/hooks/use-view-scope";
import { useOrganizationDetails } from "@/shared/hooks/api/useOrganization";
import { useWorkspace } from "@/features/Workspaces/state/WorkspaceContext";
import { SkillsService, type SkillScopeTarget } from "../services/skillsService";
import { parseSkillFacets } from "../utils/facets";
import { textoDaSkill } from "../utils/text";
import type {
  InstalledSkill,
  SkillInstallationScope,
  SkillPublisherScope,
  SkillStorefrontEntry,
} from "../types/skill";
import { SkillPublishDialog } from "@/features/Settings/components/SkillPublishDialog";
import { MagicFillProvider } from "@/shared/contexts/MagicFillContext";
import { SkillInstallerWizard } from "./SkillInstaller/SkillInstallerWizard";
import { SkillsToolbar } from "./SkillsToolbar";
import { SkillUpdateDialog } from "./SkillUpdateDialog";
import { FILTRO_VAZIO, filtrarSkills, type SkillsFilterState } from "../utils/filter";
import { invalidarSkills } from "../utils/invalidate";

/**
 * Quantas etiquetas cabem antes do "+N".
 *
 * Três é o que a coluna comporta sem quebrar linha na largura de grade mais
 * apertada. Medir a largura de verdade exigiria observar o elemento, e o ganho
 * não paga o custo num cartão que já trunca o título e a descrição.
 */
const ETIQUETAS_VISIVEIS = 3;
/**
 * A descrição do cartão, com aviso de que há mais texto quando ela é cortada.
 *
 * Componente próprio, e não um trecho do cartão, porque cada descrição precisa
 * do próprio estado de medição — e hook dentro de `.map()` é proibido pelo
 * React, além de quebrar quando a lista filtrada muda de tamanho.
 *
 * O corte é **medido**, não estimado por contagem de caracteres: ele depende da
 * largura da coluna, que muda com a grade e com o tamanho da janela. Contar
 * caracteres poria "ver mais" num cartão que mostra o texto inteiro, e um aviso
 * que mente é pior que aviso nenhum.
 */
const SkillDescription = ({ text }: { text: string }) => {
  const { t } = useTranslation();
  const ref = useRef<HTMLParagraphElement | null>(null);
  const [cortado, setCortado] = useState(false);

  useEffect(() => {
    const elemento = ref.current;
    if (!elemento) return;

    const medir = () => setCortado(elemento.scrollHeight > elemento.clientHeight + 1);

    medir();

    const observador = new ResizeObserver(medir);
    observador.observe(elemento);

    return () => observador.disconnect();
  }, [text]);

  return (
    <div className="mt-2">
      <p
        ref={ref}
        className="line-clamp-4 min-h-[4.5rem] text-[13px] leading-relaxed text-muted-foreground"
      >
        {text}
      </p>

      {/* A linha do aviso é reservada SEMPRE, mesmo quando não há aviso: ela só
          existe em alguns cartões, e sem a reserva as etiquetas dos vizinhos
          ficariam em alturas diferentes. */}
      <div className="min-h-[1.125rem]">

      {/* Só um link discreto, sem caixa nem gradiente: o cartão inteiro já é
          clicável e leva ao texto completo, então isto é AVISO, não ação. Uma
          faixa esmaecida por cima ainda brigaria com o fundo tingido do cartão
          instalado. */}
        {cortado && (
          <span className="text-[11px] text-muted-foreground underline underline-offset-2">
            {t("skills.card.more")}
          </span>
        )}
      </div>
    </div>
  );
};

interface SkillsCatalogProps {
  /** Onde esta tela opera. Vem da navegação, nunca de um campo (AB#85634, AC13). */
  target: SkillScopeTarget;
  /** Quem pode instalar e remover aqui. No espaço pessoal é sempre a própria pessoa. */
  canManage: boolean;
}
/**
 * Catálogo de Skills — **o mesmo componente nos três níveis** (AB#85634, AC23).
 *
 * Organização, workspace e espaço pessoal mostram o mesmo catálogo e o mesmo
 * cartão; o que muda é o `target`, e dele sai tanto a rota da api quanto o nível
 * em que a instalação acontece. Três telas com visual próprio divergiriam na
 * primeira mudança — e foi o que aconteceu com a área pessoal no #85633.
 *
 * A instalação é o portão: o agente de quem trabalha ali recebe só o que foi
 * instalado, e não o catálogo inteiro.
 */
export const SkillsCatalog = ({ target, canManage }: SkillsCatalogProps) => {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [selected, setSelected] = useState<SkillStorefrontEntry | null>(null);
  const [importando, setImportando] = useState(false);
  const [filtro, setFiltro] = useState<SkillsFilterState>(FILTRO_VAZIO);
  const [pendingName, setPendingName] = useState<string | null>(null);
  /** A skill cuja prévia de atualização está aberta. */
  const [atualizando, setAtualizando] = useState<SkillStorefrontEntry | null>(null);
  // A identidade de cada nível, para o cartão mostrar de ONDE a skill vem.
  const { organizationId } = useViewScope();
  const { data: organizacao } = useOrganizationDetails(organizationId ?? undefined);
  const { currentWorkspace } = useWorkspace();
  /**
   * Quem "dono" de uma instalação, para o cartão mostrar a cara dele.
   *
   * Segue o cartão de agente, que exibe os logos das integrações no canto
   * superior esquerdo: um rosto reconhecível diz de onde a coisa vem mais rápido
   * do que uma frase, e não ocupa uma linha inteira do cartão.
   *
   * O escopo individual não tem imagem própria — é a própria pessoa olhando, e
   * um avatar dela ali não acrescenta nada.
   */
  const donoDo = (scope: SkillInstallationScope) => {
    if (scope === "organization") {
      return {
        nome: organizacao?.name ?? t("skills.scope.organization"),
        logo: organizacao?.logo_url,
      };
    }
    if (scope === "product") {
      return {
        nome: currentWorkspace?.name ?? t("skills.scope.product"),
        logo: currentWorkspace?.logo_url,
      };
    }
    return { nome: t("skills.scope.user"), logo: undefined };
  };

  /**
   * O destino que ESTA tela alcança para autoria (AB#85637).
   *
   * Esta grade **é** o catálogo daquele nível — não há uma segunda tela onde a
   * skill importada apareceria. Importar daqui a põe no ar, e ela entra nesta
   * mesma grade no instante seguinte.
   *
   * O espaço pessoal é de quem está olhando, sempre. A organização é de quem a
   * administra, e `canManage` já responde isso.
   *
   * O workspace não tem catálogo no eixo de publicação, então a skill criada
   * daqui nasce no catálogo da ORGANIZAÇÃO — o mesmo ato da tela dela, sem
   * obrigar quem administra a trocar de visão, criar e voltar para instalar
   * (AB#86605). Ela aparece nesta grade, pronta para instalar no workspace. O
   * destino próprio do workspace, com proposta e aprovação, chega no #85638.
   */
  const destinoDeAutoria: SkillPublisherScope | null =
    target.scope === "user"
      ? "user"
      : (target.scope === "organization" || target.scope === "product") && canManage
        ? "organization"
        : null;

  const chave = [target.scope, target.productId ?? "-"];

  const {
    data: skills = [],
    isLoading,
    isError,
  } = useQuery<SkillStorefrontEntry[]>({
    queryKey: ["skills_storefront", ...chave],
    // Erro sobe para o react-query: devolver [] faria falha de rede parecer
    // catálogo vazio, que aqui é um estado legítimo e frequente.
    queryFn: () => SkillsService.storefront(target),
  });

  // A vitrine diz SE a skill está instalada NESTE nível (AB#85634); a listagem
  // diz com que versão e o que foi escolhido na instalação. Duas leituras, e não
  // uma: a vitrine é do catálogo e é igual para todo mundo, a listagem é de quem
  // olha.
  const { data: installed = [] } = useQuery<InstalledSkill[]>({
    queryKey: ["skills_installed", ...chave],
    queryFn: () => SkillsService.listInstalled(target),
  });

  const instalacaoDe = new Map(installed.map((entry) => [entry.name, entry]));

  // A lista inteira, e nao so as duas consultas desta tela: instalar aqui muda
  // o que a configuracao e a administracao mostram da mesma skill.
  const refresh = () => invalidarSkills(queryClient);

  /** Leva a instalação para a versão corrente do catálogo (AC8 do #85633). */
  const atualizar = async (skill: SkillStorefrontEntry) => {
    setPendingName(skill.name);
    try {
      const atualizada = await SkillsService.update(target, skill.name);
      toast({
        title: t("skills.updated.title"),
        description: t("skills.updated.description", {
          skill: textoDaSkill(skill.title, skill.title_i18n, i18n.language) || skill.name,
          version: atualizada.version,
        }),
      });
      setAtualizando(null);
      refresh();
    } catch (error) {
      toast({
        variant: "destructive",
        title: t("skills.updateFailed"),
        description: error instanceof Error ? error.message : undefined,
      });
    } finally {
      setPendingName(null);
    }
  };

  if (isLoading) {
    return (
      <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
        {[1, 2, 3].map((i) => (
          <Card key={i} className="animate-pulse">
            <CardContent className="p-6">
              <div className="h-32 rounded bg-muted"></div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <div className="py-6 text-center">
        <p className="text-destructive">{t("skills.loadFailed")}</p>
      </div>
    );
  }

  /**
   * O botão de importar, quando esta tela alcança algum destino.
   *
   * Aparece TAMBÉM com o catálogo vazio, que é justamente quando ele é mais
   * útil: não ter nada para instalar é o melhor momento para trazer a primeira
   * skill.
   */
  const importar = destinoDeAutoria && (
    // `h-9` e `shrink-0`: a mesma altura dos controles de filtro ao lado, e sem
    // ceder largura para eles — um botão de rótulo curto que encolhe vira
    // reticências.
    <Button variant="outline" className="h-9 shrink-0" onClick={() => setImportando(true)}>
      <Upload className="mr-2 h-4 w-4" />
      {t("skills.publish.action")}
    </Button>
  );

  const assistente = destinoDeAutoria && (
    <MagicFillProvider>
      <SkillPublishDialog
        open={importando}
        onOpenChange={setImportando}
        destino={destinoDeAutoria}
      />
    </MagicFillProvider>
  );

  if (skills.length === 0) {
    return (
      <div className="space-y-4 py-6 text-center">
        <p className="text-muted-foreground">{t("skills.emptyCatalog")}</p>
        {importar}
        {assistente}
      </div>
    );
  }

  const visiveis = filtrarSkills(skills, filtro);

  return (
    <div className="space-y-6">
      {/* A barra de filtros FICA COM O ESPAÇO, e o botão só ocupa o que precisa.
          Sem `flex-1` ela encolhia até o conteúdo, e o campo de busca — que
          cresce dentro dela — encolhia junto: quem filtra digita, e o campo é o
          controle mais usado da tela. `min-w-0` é o par obrigatório do `flex-1`
          aqui, senão o conteúdo interno impede a barra de ceder largura. */}
      <div className="flex flex-wrap items-start gap-3">
        <div className="min-w-0 flex-1">
          <SkillsToolbar
            skills={skills}
            value={filtro}
            onChange={setFiltro}
            total={visiveis.length}
          />
        </div>
        {importar}
      </div>

      {assistente}

      <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
        {visiveis.map((skill) => {
          const busy = pendingName === skill.name;
          const instalacao = instalacaoDe.get(skill.name);
          const etiquetas = parseSkillFacets(skill.facets);
          return (
            <Card
              key={skill.name}
              className={cn(
                "group relative flex cursor-pointer flex-col overflow-hidden",
                "border border-border/60 transition-all duration-200",
                // Sobe de leve em vez de escalar: escala borra o texto no meio da
                // transicao, e numa grade de cartoes isso aparece em todos ao
                // mesmo tempo.
                "hover:-translate-y-0.5 hover:border-border hover:shadow-lg",
                // A instalada se distingue por um ANEL e um fundo levemente
                // tingido — nao mais pela barra lateral escura, que cortava o
                // cartao ao meio e puxava o olho para a borda em vez do conteudo.
                skill.installed && "border-primary/30 bg-primary/[0.03] ring-1 ring-primary/10",
              )}
              // Instalada abre a CONFIGURAÇÃO; o resto abre a instalação.
              // Abrir o assistente de instalação para algo que já está instalado
              // oferece uma decisão que já foi tomada — e o gesto de clicar no
              // cartão é o mesmo que a engrenagem faz, então mandá-lo a lugares
              // diferentes conforme onde se acerta é que era o estranho.
              onClick={() =>
                skill.installed
                  ? navigate(`/skills/${encodeURIComponent(skill.name)}/settings`)
                  : setSelected(skill)
              }
            >
              <CardContent className="flex flex-1 flex-col p-6">
                {/* Ícone, identidade e ação na MESMA linha. Antes a ação flutuava
                    no canto e o conteúdo precisava de um recuo de 56px para passar
                    por baixo dela — recuo que, com o conteúdo alinhado à esquerda,
                    virou espaço morto no topo de todo cartão. */}
                <div className="flex items-start gap-3">
                  {/* Marca da skill: quadrado arredondado, para distinguir de um
                      agente, que é um círculo com rosto. */}
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-primary/15 to-primary/5 ring-1 ring-primary/10">
                    <Puzzle className="h-5 w-5 text-primary" />
                  </div>

                  {/* A altura é reservada no BLOCO, e não no título: no título,
                      a folga de um nome curto cairia ENTRE o título e o slug,
                      afastando dois textos que são a mesma identificação. No
                      bloco, ela cai embaixo.

                      A conta: duas linhas de 22px, mais 2px de respiro, mais os
                      ~13px do slug. */}
                  <div className="min-h-[3.75rem] min-w-0 flex-1">
                    <h3 className="line-clamp-2 text-base font-semibold leading-snug">
                      {textoDaSkill(skill.title, skill.title_i18n, i18n.language) || skill.name}
                    </h3>
                    <p className="mt-0.5 truncate font-mono text-[11px] text-muted-foreground/70">
                      {skill.name}
                    </p>
                  </div>

                  {/* A ação, e à esquerda dela a origem quando a skill está
                      instalada em outro nível. As duas juntas: são a mesma
                      pergunta — o que dá para fazer com este cartão. */}
                  <div className="flex shrink-0 items-center gap-1">
                    {skill.installed_elsewhere && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="relative h-8 w-8 rounded-full border border-border/50 bg-background p-1 shadow-sm transition-transform hover:scale-110">
                            {donoDo(skill.installed_elsewhere).logo ? (
                              <img
                                src={donoDo(skill.installed_elsewhere).logo ?? undefined}
                                alt={donoDo(skill.installed_elsewhere).nome}
                                className="h-full w-full rounded-full object-contain"
                              />
                            ) : (
                              <div className="flex h-full w-full items-center justify-center rounded-full bg-primary/5">
                                {skill.installed_elsewhere === "organization" ? (
                                  <Building2 className="h-4 w-4 text-muted-foreground" />
                                ) : skill.installed_elsewhere === "product" ? (
                                  <Users className="h-4 w-4 text-muted-foreground" />
                                ) : (
                                  <User className="h-4 w-4 text-muted-foreground" />
                                )}
                              </div>
                            )}
                          </div>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                          {t(`skills.scope.installedIn.${skill.installed_elsewhere}`)} ·{" "}
                          {donoDo(skill.installed_elsewhere).nome}
                          <br />
                          {t("skills.scope.manageWhereInstalled")}
                        </TooltipContent>
                      </Tooltip>
                    )}
                    {skill.installed_elsewhere ? (
                      // Instalada em OUTRO nível: o Instalar aparece DESABILITADO,
                      // com o motivo na dica. Um nome, um procedimento — instalar de
                      // novo aqui faria a mesma skill chegar duas vezes ao agente,
                      // que é o que a remoção da precedência quis acabar.
                      //
                      // Desabilitado e não escondido: sumir o botão deixaria a pessoa
                      // sem saber por que esta skill não oferece o que as outras
                      // oferecem. E não é a engrenagem, porque a configuração dela
                      // fica no nível onde ela foi instalada, não neste.
                      canManage && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            {/* O span existe porque botão desabilitado não emite
                                evento de mouse, e sem ele a dica — que é a única
                                explicação do estado — nunca apareceria. */}
                            <span tabIndex={0}>
                              <Button variant="outline" size="sm" disabled>
                                {t("skills.install")}
                              </Button>
                            </span>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs">
                            {t(`skills.scope.alreadyInstalledIn.${skill.installed_elsewhere}`)}
                          </TooltipContent>
                        </Tooltip>
                      )
                    ) : skill.installed ? (
                      // O selo vira MENU DE OPÇÕES (AC22, AB#86605): "instalada" é um
                      // estado que a faixa lateral já comunica, e o espaço no cartão
                      // vale mais como caminho para o que se faz com ela — configurar
                      // e, no workspace, pôr para rodar sozinha num job.
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 rounded-full"
                            aria-label={t("skills.options")}
                            title={t("skills.options")}
                            onClick={(event) => event.stopPropagation()}
                          >
                            <MoreHorizontal className="h-4 w-4 text-muted-foreground" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" onClick={(event) => event.stopPropagation()}>
                          <DropdownMenuItem
                            onSelect={() => navigate(`/skills/${encodeURIComponent(skill.name)}/settings`)}
                          >
                            <Settings className="mr-2 h-4 w-4" />
                            {t("skills.configure")}
                          </DropdownMenuItem>
                          {/* Job é do workspace e criá-lo é de quem administra
                              (AB#86605, task 86874): a tela Jobs abre o formulário
                              com a proposta montada a partir desta skill. */}
                          {target.scope === "product" && canManage && (
                            <DropdownMenuItem
                              onSelect={() =>
                                navigate(`/jobs?${new URLSearchParams({ new: "1", skill: skill.name })}`)
                              }
                            >
                              <CalendarClock className="mr-2 h-4 w-4" />
                              {t("skills.addJob")}
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    ) : (
                      canManage && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="gap-1 rounded-full px-4"
                          onClick={(event) => {
                            event.stopPropagation();
                            setSelected(skill);
                          }}
                        >
                          <Download className="h-3.5 w-3.5 text-primary" />
                          <span className="font-medium">{t("skills.install")}</span>
                        </Button>
                      )
                    )}
                  </div>
                </div>

                {/* Versão e autoria numa linha só, separadas por ponto. Eram duas
                    linhas centralizadas de texto miúdo, que somavam altura sem
                    somar informação. Quem responde pela skill importa porque o
                    catálogo recebe procedimento de terceiro. */}
                {/* Quando quem escreveu tem conta no Prodgy, aparece o rosto:
                    num catálogo que recebe procedimento de terceiro, "fulano
                    escreveu isto" decide mais que "alguém publicou isto". Sem
                    perfil, sobra o crédito em texto do próprio pacote. */}
                <div className="mt-4 flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground">
                  {/* A versão da INSTALAÇÃO quando ela existe, e a do catálogo
                      quando não.

                      O cartão lia sempre a da vitrine — a corrente do catálogo —
                      e o resultado era dizer "Versão 3" ao lado de um botão
                      "Atualizar para a v3". A skill instalada está presa à versão
                      que ela fixou; é essa que descreve o que roda hoje. */}
                  <span className="shrink-0">
                    {t("skills.version", { version: instalacao?.version ?? skill.version })}
                  </span>
                  <span className="shrink-0">·</span>

                  {skill.owner_profile ? (
                    <>
                      <Avatar className="h-4 w-4 shrink-0">
                        <AvatarImage
                          src={skill.owner_profile.avatar_url ?? undefined}
                          alt={skill.owner_profile.name ?? ""}
                        />
                        <AvatarFallback className="text-[9px]">
                          {(skill.owner_profile.name ?? "?").slice(0, 1).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <span className="truncate">{skill.owner_profile.name ?? skill.author}</span>
                    </>
                  ) : skill.author ? (
                    /* O crédito declarado no pacote. Sem logo: ele costuma trazer
                       o nome de OUTRA casa, e a marca do Prodgy ao lado diria
                       que a skill é nossa quando não é. */
                    <span className="truncate">{skill.author}</span>
                  ) : skill.publisher_scope === "platform" ? (
                    /* Nada declarado e publicada pela plataforma: a dona é a casa,
                       e a marca ocupa o lugar do rosto — do mesmo jeito que o
                       avatar ocupa quando é gente. */
                    <>
                      <Avatar className="h-4 w-4 shrink-0">
                        <AvatarImage src="/assets/logo.png" alt="Prodgy" />
                        <AvatarFallback className="text-[9px]">P</AvatarFallback>
                      </Avatar>
                      <span className="truncate">Prodgy</span>
                    </>
                  ) : (
                    <span className="truncate">{t("skills.publisher.organization")}</span>
                  )}
                </div>

                {/* Quatro linhas. Com duas, a descrição parava antes de dizer
                    QUANDO usar a skill — que é a parte pela qual se escolhe uma. */}
                {/* O título e a descrição são o que o PRODGY acrescenta em
                    volta do pacote, e por isso têm idioma (AC37). O conteúdo da
                    skill, não. */}
                <SkillDescription
                  text={textoDaSkill(skill.description, skill.description_i18n, i18n.language)}
                />

                {/* `mt-auto` empurra as etiquetas para o rodapé: elas passam a
                    alinhar entre os cartões da fileira, mesmo com descrições de
                    tamanhos diferentes. */}
                <div className="mt-auto pt-4">
                  {/* As etiquetas da skill (AB#85634), logo abaixo da descrição.
                      Ficam em UMA linha: a grade tem três colunas, e um cartão
                      que cresce por causa de etiqueta desalinha a fileira toda.
                      O que não cabe vira "+N", com o resto na dica. */}
                  {etiquetas.length > 0 && (
                    <div className="flex w-full flex-nowrap items-center gap-1 overflow-hidden">
                      {etiquetas.slice(0, ETIQUETAS_VISIVEIS).map((etiqueta) => (
                        <Tooltip key={etiqueta.raw}>
                          <TooltipTrigger asChild>
                            <Badge
                              variant="secondary"
                              className="max-w-[9rem] shrink-0 truncate text-[11px] font-normal"
                            >
                              {etiqueta.value}
                            </Badge>
                          </TooltipTrigger>
                          <TooltipContent>
                            {etiqueta.name}: {etiqueta.value}
                          </TooltipContent>
                        </Tooltip>
                      ))}
                      {etiquetas.length > ETIQUETAS_VISIVEIS && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Badge variant="outline" className="shrink-0 text-[11px] font-normal">
                              +{etiquetas.length - ETIQUETAS_VISIVEIS}
                            </Badge>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs">
                            {etiquetas
                              .slice(ETIQUETAS_VISIVEIS)
                              .map((etiqueta) => `${etiqueta.name}: ${etiqueta.value}`)
                              .join(" · ")}
                          </TooltipContent>
                        </Tooltip>
                      )}
                    </div>
                  )}

                  {/* Versão nova no catálogo: avisa e abre a prévia — nunca
                      troca sozinho, e nem troca sem dizer o que muda (AC8 do
                      #85633). A skill é um procedimento que o time validou;
                      mudá-lo por baixo mudaria o resultado.

                      Fica no canto inferior DIREITO, em linha própria: na
                      linha das etiquetas ele comia a última delas. O rótulo é
                      só o verbo e a frase inteira vive na dica — é um aviso, e
                      não o assunto do cartão. Sem o número, porque o destino só
                      se decide na prévia. */}
                  {instalacao?.available_version &&
                    canManage &&
                    instalacao.scope === target.scope && (
                      <div className="mt-3 flex justify-end">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 gap-1.5 px-2 text-xs font-medium text-primary hover:bg-primary/10 hover:text-primary"
                              disabled={busy}
                              onClick={(event) => {
                                event.stopPropagation();
                                setAtualizando(skill);
                              }}
                            >
                              {t("skills.update.badge")}
                              {busy ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <ArrowUpCircle className="h-4 w-4" />
                              )}
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs">
                            {t("skills.update.action")}{" "}
                            {t("skills.update.hint", {
                              version: instalacao.version,
                            })}
                          </TooltipContent>
                        </Tooltip>
                      </div>
                    )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* A mesma prévia da tela de configuração: o caminho curto do card
          não pode ser o caminho que pula o aviso. */}
      {atualizando && (
        <SkillUpdateDialog
          open
          onOpenChange={(aberto) => !aberto && setAtualizando(null)}
          target={target}
          name={atualizando.name}
          saving={pendingName === atualizando.name}
          onConfirm={() => atualizar(atualizando)}
        />
      )}

      <SkillInstallerWizard
        skill={selected}
        target={target}
        canManage={canManage}
        onOpenChange={(open) => !open && setSelected(null)}
        onChanged={refresh}
      />
    </div>
  );
};

export default SkillsCatalog;
